import { useEffect } from 'react';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import dayjs from 'dayjs';
import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { string, object, boolean } from 'zod/v4';

import { appConfig } from '@/config/appConfig';
import { homeRoutes } from '@/features/home/routes/homeRoutes';
import useRegexPatterns from '@/features/regexPatterns/hooks/useRegexPatterns';
import { getValidationErrorMessage } from '@/helpers';
import useI18n from '@/hooks/useI18n';
import useNavigation from '@/hooks/useNavigation';
import usePost from '@/hooks/usePost';
import type { ErrorResponse } from '@/types/ErrorResponse';

import { authRoutes } from '../routes/authRoutes';
import type { LoginRequestBody } from '../types/LoginRequestBody';
import type { LoginResponse } from '../types/LoginResponse';

import useAuthStore from './useAuthStore';

export type FormValues = {
    identifier: string;
    password: string;
    rememberMe: boolean;
};

type UseLoginReturn = {
    isValid: boolean;
    isLoading: boolean;
    response: LoginResponse | null;
    error: ErrorResponse | null;
    validationErrors: FieldErrors<FormValues>;
    submit: () => void;
    register: UseFormRegister<FormValues>;
    setValue: UseFormSetValue<FormValues>;
    watch: UseFormWatch<FormValues>;
};

export default function useLogin(): UseLoginReturn {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();
    const { replace } = useNavigation();
    const { regexPatterns } = useRegexPatterns();

    const updateUser = useAuthStore((state) => state.updateUser);
    const updateToken = useAuthStore((state) => state.updateToken);
    const { submit: submitHandler, response, isLoading, error, validationErrors } = usePost<LoginResponse, ErrorResponse>();

    const schema = object({
        identifier: string({ error: t('Auth.Validations.Identifier_Required') })
            .min(4, { error: t('Auth.Validations.Identifier_Minimum_Length') })
            .max(255, { error: t('Auth.Validations.Identifier_Maximum_Length') }),
        password: string({ error: t('Auth.Validations.Password_Required') })
            .min(8, { error: t('Auth.Validations.Password_Minimum_Length') })
            .max(64, { error: t('Auth.Validations.Password_Maximum_Length') })
            .regex(regexPatterns?.users.password ?? /^/, {
                error: t('Auth.Validations.Password_Regex_Error'),
            }),
        rememberMe: boolean({ error: t('Auth.Validations.Remember_Me_Required') }),
    });

    const {
        register,
        formState: { errors, isValid },
        setError,
        watch,
        setValue,
    } = useForm({
        resolver: standardSchemaResolver(schema),
        mode: appConfig.formValidation.mode,
        defaultValues: {
            identifier: '',
            password: '',
            rememberMe: true,
        },
    });

    const { identifier, password, rememberMe } = watch();

    // ----------------------------------------------------------------------------------------------------
    // MARK: Functions
    // ----------------------------------------------------------------------------------------------------
    function submit(): void {
        const requestBody: LoginRequestBody = {
            identifier,
            password,
            rememberMe,
        };

        submitHandler(authRoutes.api.login(), requestBody, {
            onSuccess: (res) => {
                updateUser(res.user, { expires: rememberMe ? dayjs().add(7, 'days').toDate() : dayjs().endOf('day').toDate() });
                updateToken(res.token, { expires: rememberMe ? dayjs().add(7, 'days').toDate() : dayjs().endOf('day').toDate() });
                replace(homeRoutes.app.index());
            },
        });
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Side Effects
    // ----------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (validationErrors.length > 0) {
            setError('identifier', { message: getValidationErrorMessage('identifier', validationErrors) });
            setError('password', { message: getValidationErrorMessage('password', validationErrors) });
            setError('rememberMe', { message: getValidationErrorMessage('remember_me', validationErrors) });
        }
    }, [validationErrors, setError]);

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        isValid,
        isLoading,
        response,
        error,
        validationErrors: errors,
        submit,
        register,
        setValue,
        watch,
    };
}
